import { VaultsTable } from './VaultsTable';
import { useMemo, useRef, useEffect, useState } from 'react';
import { useDrawersStore } from 'src/stores/useDrawersStore';
import Select from 'src/components/primitives/Select/Select';
import { useVaultsStore } from 'src/stores/vaultsStore/useVaultsStore';
import { useAllVaultStats } from 'src/hooks/query/vaults/derived/useVaultStats';
import Tabs from 'src/components/primitives/Tabs/Tabs';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import { cm } from 'src/utils/classNames';
import { MyVaultPositionsOverview } from '../MyVaultPositionsOverview';
import { MyVaultPositionsContent } from './MyVaultPositionsTable';
import VerifiedVaultsCallout from '../VerifiedVaultsCallout';
import CommunityVaultsCallout from '../CommunityVaultsCallout';
import { useVaultStatsWithDepositors } from 'src/hooks/vaults/useVaultStatsWithDepositors';
import { useVaultManagerOptions } from 'src/hooks/vaults/useVaultManagerOptions';
import { useSpotMarketsLookup } from 'src/hooks/spotMarkets/useSpotMarketsLookup';
import Button from 'src/components/primitives/Button/Button';
import { MySortableHeaderType, SortableHeaderType } from '../type';
import ScrollShadows from 'src/components/ScrollShadows';
import { PeriodApy } from 'src/@types/vaults';
import { useVaultDepositorAccounts } from 'src/hooks/query/vaults/base/useVaultDepositorAccounts';
import usePostHogCapture from 'src/hooks/posthog/usePostHogCapture';

function getApyOptions(): { value: PeriodApy; label: string }[] {
	return [
		{ value: '7d', label: '7D' },
		{ value: '30d', label: '30D' },
		{ value: '90d', label: '90D' },
	];
}

export const VaultsTables = () => {
	const showDrawer = useDrawersStore((s) => s.showDrawer);
	const isMobile = useIsMobileScreenSize();
	const {
		getVaultDisplaySettings,
		activeTab,
		setActiveTab,
		setVaultDisplaySettings,
	} = useVaultsStore((s) => ({
		getVaultDisplaySettings: s.getVaultDisplaySettings,
		activeTab: s.activeTab,
		setActiveTab: s.setActiveTab,
		setVaultDisplaySettings: s.setVaultDisplaySettings,
	}));
	const vaultDisplaySettings = getVaultDisplaySettings();
	const { captureEvent } = usePostHogCapture();

	const activeFiltersCount = (() => {
		let count = 0;
		if (vaultDisplaySettings.targetVaultManager !== 'All') {
			count++;
		}
		if (vaultDisplaySettings.targetDepositAsset !== 'All') {
			count++;
		}
		if (vaultDisplaySettings.targetApyPeriod !== '90d') {
			count++;
		}
		return count;
	})();

	const vaultStatsWithDepositors = useVaultStatsWithDepositors();
	const hasPositions = useMemo(() => {
		return vaultStatsWithDepositors.some((stat) => stat.depositor !== null);
	}, [vaultStatsWithDepositors]);

	const spotMarketsLookup = useSpotMarketsLookup();
	const targetVaultManager = vaultDisplaySettings.targetVaultManager;
	const setTargetVaultManager = (option: string) => {
		setVaultDisplaySettings((draft) => {
			draft.targetVaultManager = option;
		});
	};
	const targetDepositAsset = vaultDisplaySettings.targetDepositAsset;
	const setTargetDepositAsset = (option: string) => {
		setVaultDisplaySettings((draft) => {
			draft.targetDepositAsset = option;
		});
	};
	const targetApyPeriod = vaultDisplaySettings.targetApyPeriod;
	const setTargetApyPeriod = (option: PeriodApy) => {
		setVaultDisplaySettings((draft) => {
			draft.targetApyPeriod = option;
		});
	};
	const [mySortBy, setMySortBy] = useState<MySortableHeaderType>('apy');

	const setSortBy = (option: SortableHeaderType) => {
		if (activeTab === 'my_positions') {
			setMySortBy(option as MySortableHeaderType);
		} else {
			setVaultDisplaySettings((draft) => {
				draft.sortedBy = option;
			});
		}
	};

	const { data } = useAllVaultStats();
	const vaultsStatsList = data.list;

	const { data: authorityDepositedVaults } = useVaultDepositorAccounts();

	const vaultDepositorAccounts = useMemo(() => {
		if (!authorityDepositedVaults) return [];
		return Object.values(authorityDepositedVaults).map(
			(vault) => vault.vaultDepositor
		);
	}, [authorityDepositedVaults]);

	const tabs = useMemo(() => {
		const result = [];

		result.push({
			label: isMobile ? 'Verified Managers' : 'Verified Vault Managers',
			value: 'verified' as const,
		});

		result.push({
			label: isMobile ? 'Ecosystem Managers' : 'Ecosystem Vault Managers',
			value: 'community' as const,
		});

		if (hasPositions) {
			result.push({
				label: 'Your Positions',
				value: 'my_positions' as const,
			});
		}

		return result;
	}, [hasPositions, isMobile]);

	const vaultManagerOptions = useVaultManagerOptions(activeTab);

	function getDepositAssetOptions() {
		return [
			{ value: 'All', label: 'All' },
			...Array.from(
				new Set(
					vaultsStatsList.map(
						(vaultStats) => vaultStats.uiVaultConfig.depositAsset
					)
				)
			).map((asset) => ({
				value: spotMarketsLookup[asset].marketIndex,
				label: spotMarketsLookup[asset].symbol,
			})),
		];
	}

	const handleOpenFiltersDrawer = () => {
		showDrawer('vaultDisplayFilters');
	};

	const scrollableTabsRef = useRef<HTMLDivElement>(null);
	const depositAssetOptions = getDepositAssetOptions();
	const apyOptions = getApyOptions();
	const sortByOptions: {
		value: SortableHeaderType | MySortableHeaderType;
		label: string;
	}[] =
		activeTab === 'my_positions'
			? [
					{ value: 'apy', label: 'APY' },
					{ value: 'balance', label: 'Balance' },
					{ value: 'earnings', label: 'Earnings' },
				]
			: [
					{ value: 'apy', label: 'APY' },
					{ value: 'tvl', label: 'TVL' },
					{ value: 'age', label: 'Age' },
					{ value: 'balance', label: 'Balance' },
				];

	const displaySelectionTabs = tabs.length > 1;

	const resetFiltersOnTabSwitch = () => {
		setVaultDisplaySettings((draft) => {
			draft.targetVaultManager = 'All';
		});
	};

	useEffect(() => {
		resetFiltersOnTabSwitch();
	}, [activeTab]);

	useEffect(() => {
		const scrollWrapper = document.getElementById(
			'nav-footer-wrapper-middle-content-scroll-wrapper'
		);
		if (scrollWrapper) {
			scrollWrapper.scrollTo({ top: 0 });
		}
	}, [
		activeTab,
		targetApyPeriod,
		vaultDisplaySettings.targetApyPeriod,
		vaultDisplaySettings.targetVaultManager,
		vaultDisplaySettings.targetDepositAsset,
		vaultDisplaySettings.sortedBy,
		mySortBy,
	]);

	return (
		<div className="flex w-full flex-col items-start gap-4 sm:px-0">
			<div className="sticky top-0 z-50 -mb-lg flex w-full flex-col items-center gap-lg bg-main-bg pb-lg">
				<div className="flex w-full flex-col items-center gap-4 border-b border-stroke-divider">
					{displaySelectionTabs && (
						<div
							className={cm('sm:w-fit', tabs.length > 2 ? 'w-full' : 'w-fit')}
						>
							<ScrollShadows
								scrollableRef={scrollableTabsRef}
								gradientFromClassName="from-main-bg w-[60px]"
							>
								<div
									ref={scrollableTabsRef}
									className="hidden-scroll flex justify-start overflow-x-auto"
								>
									<Tabs
										className="w-fit"
										variant="primary"
										size="lg"
										activeTab={activeTab}
										options={tabs}
										onTabChange={(tab) => {
											setActiveTab(tab);

											if (tab === 'community') {
												captureEvent('on_page_eco_vault');
											}
										}}
									/>
								</div>
							</ScrollShadows>
						</div>
					)}
				</div>
				<div className="flex w-full flex-col flex-wrap justify-between gap-y-md bg-main-bg sm:relative sm:flex-row">
					{!isMobile && (
						<div className="flex items-center justify-start gap-2">
							<Select.Root
								value={targetVaultManager}
								onValueChange={setTargetVaultManager}
							>
								<Select.Trigger size="sm" label="Manager" className="w-[200px]">
									{vaultManagerOptions.find(
										(opt) => opt.value === targetVaultManager
									)?.label || 'All'}
								</Select.Trigger>
								<Select.Options className="thin-scroll max-h-[225px] overflow-y-auto">
									{vaultManagerOptions.map((option) => (
										<Select.Option key={option.value} value={option.value}>
											{option.label}
										</Select.Option>
									))}
								</Select.Options>
							</Select.Root>

							<Select.Root
								value={String(targetDepositAsset)}
								onValueChange={(value) => setTargetDepositAsset(value)}
							>
								<Select.Trigger
									size="sm"
									label="Deposit Asset"
									className="w-[200px]"
								>
									{depositAssetOptions.find(
										(opt) => String(opt.value) === String(targetDepositAsset)
									)?.label || 'All'}
								</Select.Trigger>
								<Select.Options className="thin-scroll max-h-[225px] overflow-y-auto">
									{depositAssetOptions.map((option) => (
										<Select.Option
											key={String(option.value)}
											value={String(option.value)}
										>
											{option.label}
										</Select.Option>
									))}
								</Select.Options>
							</Select.Root>

							<Select.Root
								value={targetApyPeriod}
								onValueChange={setTargetApyPeriod}
							>
								<Select.Trigger size="sm" label="APY" className="w-[120px]">
									{apyOptions.find((opt) => opt.value === targetApyPeriod)
										?.label || '90D'}
								</Select.Trigger>
								<Select.Options>
									{apyOptions.map((option) => (
										<Select.Option key={option.value} value={option.value}>
											{option.label}
										</Select.Option>
									))}
								</Select.Options>
							</Select.Root>
						</div>
					)}
					{isMobile && (
						<div className="flex flex-row items-center justify-between gap-sm">
							<Select.Root
								value={
									activeTab === 'my_positions'
										? mySortBy
										: vaultDisplaySettings.sortedBy
								}
								onValueChange={(value) =>
									setSortBy(value as SortableHeaderType)
								}
							>
								<Select.Trigger className="w-full" size="sm" label="Sort by">
									{
										sortByOptions.find(
											(opt) =>
												opt.value ===
												(activeTab === 'my_positions'
													? mySortBy
													: vaultDisplaySettings.sortedBy)
										)?.label
									}
								</Select.Trigger>
								<Select.Options>
									{sortByOptions.map((option) => (
										<Select.Option key={option.value} value={option.value}>
											{option.label}
										</Select.Option>
									))}
								</Select.Options>
							</Select.Root>
							<Button
								className="w-full"
								variant="secondary"
								size="md"
								onClick={handleOpenFiltersDrawer}
							>
								Filters
								{activeFiltersCount > 0 && ` (${activeFiltersCount})`}
							</Button>
						</div>
					)}

					{activeTab === 'my_positions' && <MyVaultPositionsOverview />}
				</div>
			</div>
			<div className="flex w-full flex-col gap-lg">
				{activeTab === 'verified' && <VerifiedVaultsCallout />}

				{activeTab === 'community' && <CommunityVaultsCallout />}

				{activeTab === 'my_positions' ? (
					<MyVaultPositionsContent
						type={activeTab}
						targetVaultManager={targetVaultManager}
						targetDepositAsset={targetDepositAsset}
						targetApyPeriod={targetApyPeriod}
						vaultDepositorAccounts={vaultDepositorAccounts}
						sortType={mySortBy}
						setSortType={setMySortBy}
					/>
				) : (
					<VaultsTable
						hidden={false}
						type={activeTab}
						targetVaultManager={targetVaultManager}
						targetDepositAsset={targetDepositAsset}
						targetApyPeriod={targetApyPeriod}
						vaultDepositorAccounts={vaultDepositorAccounts}
					/>
				)}
				<div className="typo-b5 text-text-secondary">
					Disclaimer: some vaults are created and managed by independent
					contributors, not by Drift. It is important to carefully understand
					and accept all risks associated with the platforms, protocols, and
					underlying details before engaging with Vaults.
				</div>
			</div>
		</div>
	);
};
